import { computed } from 'vue';
import { useStore } from '@/utils/vue-api';
import { IR_ECHARTS_COLORS } from '@/utils/enum';
import i18n, { EN } from '@/assets/js/i18n';

// 管理子模块的指标字段
export function useFinanceMap() {
	const store = useStore();

	const getIrThemeColor = computed(() => store.getters.getIrThemeColor);
	const getThemeColors = computed(() => IR_ECHARTS_COLORS[getIrThemeColor.value]);

	const getIrFullCode = computed(() => store.getters.getIrFullCode);
	const isHKStock = computed(() => getIrFullCode.value.startsWith('hk'));

	const isEN = computed(() => i18n.locale === EN);

	// 处理英文环境下的单位显示
	const processUnitForEN = unit => {
		if (!unit) return unit;
		// 中文单位列表
		const chineseUnits = ['次'];
		// 如果是英文环境且单位是中文内容，则返回空字符串
		if (isEN.value && chineseUnits.includes(unit)) {
			return '';
		}
		return unit;
	};
	// 批量处理配置对象中的 unit 属性
	const processConfigUnits = config => {
		if (!config || typeof config !== 'object') return config;

		const processedConfig = {};
		for (const [key, value] of Object.entries(config)) {
			if (value && typeof value === 'object') {
				// 如果是对象且有 unit 属性，处理 unit
				if (Object.prototype.hasOwnProperty.call(value, 'unit')) {
					processedConfig[key] = {
						...value,
						unit: processUnitForEN(value.unit)
					};
				} else {
					// 递归处理嵌套对象
					processedConfig[key] = processConfigUnits(value);
				}
			} else {
				processedConfig[key] = value;
			}
		}
		return processedConfig;
	};

	const mainIndex = computed(() => ({
		gross_profit_rate: {
			name: '毛利率',
			key: 'grossMargin',
			yoyKey: 'grossMarginYOY',
			unit: '%'
		},
		net_profit_ratio: {
			name: '净利率',
			key: 'npMargin',
			yoyKey: 'npMarginYOY',
			unit: '%'
		},
		return_on_equity: { name: '净资产收益率', key: 'roe', yoyKey: 'roeYOY', unit: '%' },
		roa: { name: '资产回报率', key: 'roa', yoyKey: 'roaYOY', unit: '%' },

		turnover_of_account_receivable: {
			name: '应收账款周转率',
			key: 'arTurnover',
			yoyKey: 'arTurnoverYOY',
			unit: '次'
		},
		rate_of_stock_turnover: {
			name: '存货周转率',
			key: 'invenTurnover',
			yoyKey: 'invenTurnoverYOY',
			unit: '次'
		},
		current_asset_turnover: {
			name: '流动资产周转率',
			key: 'caTurnover',
			yoyKey: 'caTurnoverYOY',
			unit: '次'
		},
		turnover_of_fixed_assets: {
			name: '固定资产周转率',
			key: 'faTurnover',
			yoyKey: 'faTurnoverYOY',
			unit: '次'
		},
		total_assets_turnover: {
			name: '总资产周转率',
			key: 'taTurnover',
			yoyKey: 'taTurnoverYOY',
			unit: '次'
		},

		increase_rate_of_business_revenue: { name: '营业收入增长率', key: 'revenueYoy', unit: '%' },
		operating_profit_growth_rate: { name: '营业利润增长率', key: 'operProfitYoy', unit: '%' },
		net_profit_growth_rate: { name: '净利润增长率', key: 'niYoy', unit: '%' },
		growth_rate_of_total_assets: { name: '总资产增长率', key: 'taYtd', unit: '%' },
		rate_of_capital_accumulation: { name: '资本积累率', key: 'naYtd', yoyKey: 'naYtdYOY', unit: '%' },

		liquidity_ratio: { name: '流动比率', key: 'currentRatio', yoyKey: 'currentRatioYOY' },
		quick_ratio: { name: '速动比率', key: 'quickRatio', yoyKey: 'quickRatioYOY' },
		balance_sheet_ratios: {
			name: '资产负债比率',
			key: 'assetLiabRatio',
			yoyKey: 'assetLiabRatioYOY',
			unit: '%'
		},

		net_asset: { name: '每股净资产', key: 'nAssetPs', yoyKey: 'nAssetPsYOY' },
		cash_flow: { name: '每股经营现金流', key: 'nCfOperAPs', yoyKey: 'nCfOperAPsYOY' },
		earnings_per_share: { name: '基本每股收益', key: 'basicEps', yoyKey: 'basicEpsYOY' },
		provident_fund_per_share: { name: '每股公积金', key: 'reserPs', yoyKey: 'reserPsYOY' },
		undistributed_per_share: { name: '每股未分配', key: 'rePs', yoyKey: 'rePsYOY' }
	}));

	const profitStatement = computed(() => ({
		operation_revenue: { name: '营业收入', key: 'revenue', yoyKey: 'revenueYOY' },
		operating_profit: { name: '营业利润', key: 'operateProfit', yoyKey: 'operateProfitYOY' },
		total_profit: { name: '利润总额', key: 'tProfit', yoyKey: 'tProfitYOY' },
		net_profit_rate: { name: '净利润', key: 'nIncome', yoyKey: 'nIncomeYOY' },
		net_profit: { name: '归母净利润', key: 'nIncomeAttrP', yoyKey: 'nIncomeAttrPYOY' }
	}));

	const balanceSheet = computed(() => {
		const asset = [
			{
				name: i18n.t('enterprise.non-current-assets'),
				key: 'tNca',
				color: getThemeColors.value[1]
			},
			{
				name: i18n.t('enterprise.current-assets'),
				key: 'tCa',
				color: getThemeColors.value[0]
			}
		];
		const liabilities = [
			{
				name: i18n.t('enterprise.non-current-liabilitise'),
				key: 'tNcl',
				color: getThemeColors.value[1]
			},
			{
				name: i18n.t('enterprise.current-liabilitise'),
				key: 'tCl',
				color: getThemeColors.value[0]
			}
		];
		const others = isHKStock.value
			? [{ name: i18n.t('enterprise.owners-equity'), key: 'tShEquity' }]
			: [
					{ name: i18n.t('enterprise.additional-paid-in-Capital'), key: 'capitalReser' },
					{ name: i18n.t('enterprise.legal-reserve'), key: 'surplusReser' },
					{ name: i18n.t('enterprise.retained-profit'), key: 'retainedEarnings' },
					{ name: i18n.t('enterprise.owners-equity'), key: 'tShEquity' }
			  ];
		return { asset, liabilities, others };
	});

	const cashFlowStatement = computed(() => ({
		default: [
			{
				name: i18n.t('enterprise.operating-cash-flow'),
				key: 'nCfOperateA'
			},
			{
				name: i18n.t('enterprise.investing-cash-flow'),
				key: 'nCfFrInvestA'
			},
			{
				name: i18n.t('enterprise.finaning-cash-flow'),
				key: 'nCfFrFinanA'
			}
		]
	}));

	const registry = {
		main_index: () => processConfigUnits(mainIndex.value),
		profit_statement: () => profitStatement.value,
		balance_sheet: () => balanceSheet.value,
		cash_flow_statement: () => cashFlowStatement.value
	};
	const getModuleMap = moduleId => registry[moduleId]?.() || {};

	return {
		getModuleMap
	};
}
